/**
 * Bulk-etikettskanning: velg/slipp mange etikettbilder, behandle ett og ett
 * mot label-scan API (ett kall per bilde), vis resultater per rad med legg-til.
 * Modellert på receipt-bulk.tsx; bruker ScanResultCard for hvert resultat.
 */
import { useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Wines, Bottles, Labels } from '@/lib/api';
import { Badge, Button, Card, EmptyState, Icon, Spinner } from '@/design-system';
import { AddBottleDrawer } from '@/components/AddBottleDrawer';
import { ScanResultCard, extractionToPrefill } from '@/components/ScanResultCard';
import { usePrefs, useToasts } from '@/lib/store';
import { useQueryClient } from '@tanstack/react-query';
import type { ScanResponse } from '@/lib/api/wines';
import type { VinmonopoletProduct, BottleCreate } from '@/lib/api';

// ─── Types ────────────────────────────────────────────────────────────────────

type ItemStatus = 'pending' | 'scanning' | 'done' | 'error' | 'added';

interface BulkItem {
  id: string;
  file: File;
  previewUrl: string;
  status: ItemStatus;
  scanData?: ScanResponse;
  errorMessage?: string;
  addedBottleId?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('Fil-lesing feilet'));
    reader.readAsDataURL(file);
  });
}

async function fileToBase64(file: File): Promise<string> {
  const dataUrl = await readFileAsDataUrl(file);
  return dataUrl.split(',')[1] ?? dataUrl;
}

function itemId(file: File, index: number): string {
  return `bulk-${index}-${file.name.slice(0, 16)}-${file.size}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ScanBulkLabelsPage() {
  const inputId = useId();
  const dropRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const { push } = useToasts();
  const { printTarget } = usePrefs();

  const [items, setItems] = useState<BulkItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);

  // AddBottleDrawer state — also tracks which item triggered it
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPrefill, setDrawerPrefill] = useState<Partial<BottleCreate>>({});
  const [drawerSourceId, setDrawerSourceId] = useState<string | null>(null);

  const labelQueueEnabled = Boolean(printTarget);

  // ── Update a single item immutably ────────────────────────────────────────
  const patchItem = (id: string, patch: Partial<BulkItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  // ── Add files from picker or drop ────────────────────────────────────────
  const addFiles = async (files: FileList | File[]) => {
    const arr = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (arr.length === 0) return;
    const startIdx = items.length;
    const newItems: BulkItem[] = await Promise.all(
      arr.map(async (file, i) => ({
        id: itemId(file, startIdx + i),
        file,
        previewUrl: await readFileAsDataUrl(file),
        status: 'pending' as ItemStatus,
      })),
    );
    setItems((prev) => [...prev, ...newItems]);
  };

  // ── Scan one item (by ref so it always sees fresh status) ─────────────────
  const scanSingleItem = async (item: BulkItem) => {
    patchItem(item.id, { status: 'scanning', errorMessage: undefined });
    try {
      const base64 = await fileToBase64(item.file);
      const data = await Wines.scanLabel({ imageBase64: base64, mimeType: item.file.type, candidateLimit: 6 });
      patchItem(item.id, { status: 'done', scanData: data });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      patchItem(item.id, { status: 'error', errorMessage: msg });
    }
  };

  // ── Scan all items with a given status (batched, 3 concurrent) ───────────
  const scanByStatus = (statusFilter: ItemStatus) => {
    const targets = items.filter((it) => it.status === statusFilter);
    void (async () => {
      const BATCH = 3;
      for (let i = 0; i < targets.length; i += BATCH) {
        await Promise.allSettled(targets.slice(i, i + BATCH).map(scanSingleItem));
      }
    })();
  };

  const scanAll = () => scanByStatus('pending');
  const retryAllFailed = () => scanByStatus('error');

  // ── Queue QR labels ───────────────────────────────────────────────────────
  const queueLabels = async (bottleIds: string[]) => {
    if (!labelQueueEnabled || bottleIds.length === 0) return;
    try {
      await Labels.printBottles({
        bottleIds,
        includeQr: true,
        includeBarcode: true,
        printerName: printTarget?.printerName,
        deliveryTarget: printTarget ?? undefined,
      });
      void qc.invalidateQueries({ queryKey: ['labels.queue'] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      push({ title: 'Etikettkø feilet', description: msg, tone: 'danger' });
    }
  };

  // ── Add a single matched product from a scan card ─────────────────────────
  const handleConfirmMatch = async (itemId: string, product: VinmonopoletProduct, vintage: string | undefined) => {
    setAddingId(itemId);
    const country = (product.origins as { country?: string } | undefined)?.country;
    const region = (product.origins as { region?: string } | undefined)?.region;
    try {
      const bottle = await Bottles.create({
        productId: product.basic.productId,
        catalogId: product.basic.productId,
        name: product.basic.productLongName ?? product.basic.productShortName ?? 'Ukjent',
        vintage: vintage?.trim() || product.basic.vintage,
        country,
        region,
        photoUrl: product.imageUrl,
        qty: 1,
      });
      await queueLabels([bottle.id]);
      void qc.invalidateQueries({ queryKey: ['cellar.bottles'] });
      patchItem(itemId, { status: 'added', addedBottleId: bottle.id });
      push({ title: labelQueueEnabled ? 'Lagt i kjelleren og etikettkø' : 'Lagt i kjelleren', description: bottle.name, tone: 'success' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      push({ title: 'Kunne ikke legge til', description: msg, tone: 'danger' });
    } finally {
      setAddingId(null);
    }
  };

  // ── Open manual drawer, track source item ────────────────────────────────
  const handleManualAdd = (prefill: Record<string, unknown>, sourceItemId: string) => {
    setDrawerPrefill(prefill as Partial<BottleCreate>);
    setDrawerSourceId(sourceItemId);
    setDrawerOpen(true);
  };

  // ── Add all with matches ──────────────────────────────────────────────────
  const addAllMatches = async () => {
    const readyItems = items.filter((it) => {
      if (it.status !== 'done') return false;
      return (it.scanData?.candidates?.length ?? 0) > 0;
    });
    for (const it of readyItems) {
      const best = it.scanData?.candidates[0];
      if (best) {
        // eslint-disable-next-line no-await-in-loop
        await handleConfirmMatch(it.id, best.product, it.scanData?.extraction.vintage);
      }
    }
  };

  // ── Drag-and-drop ─────────────────────────────────────────────────────────
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    void addFiles(e.dataTransfer.files);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  };

  const onDragLeave = () => setDragging(false);

  // ── Counts ────────────────────────────────────────────────────────────────
  const pendingCount = items.filter((it) => it.status === 'pending').length;
  const scanningCount = items.filter((it) => it.status === 'scanning').length;
  const errorCount = items.filter((it) => it.status === 'error').length;
  const doneWithMatch = items.filter((it) => it.status === 'done' && (it.scanData?.candidates?.length ?? 0) > 0).length;
  const addedCount = items.filter((it) => it.status === 'added').length;

  return (
    <div className="grid gap-6 max-w-2xl">
      <header className="grid gap-2">
        <Link to="/scan" className="text-xs uppercase tracking-widest text-mist hover:text-candle">
          ← Tilbake til skann
        </Link>
        <h1 className="ui-section-title text-3xl">Bulk etikettskanning</h1>
        <p className="text-sm text-mist">
          Velg eller slipp mange etikettbilder — vi analyserer hvert enkelt og finner katalogtref.
          Legg til i kjelleren enten enkeltvis eller alle på én gang.
        </p>
      </header>

      {/* Drop zone */}
      <Card className="p-4">
        <div
          ref={dropRef}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          className={[
            'grid gap-3 cursor-pointer rounded-md border-2 border-dashed p-6 text-center transition-colors',
            dragging ? 'border-candle bg-candle/5' : 'border-line hover:border-candle/50',
          ].join(' ')}
        >
          <Icon name="camera" size={28} className="mx-auto text-candle" />
          <span className="font-serif text-base text-parchment">
            Slipp bilder her, eller velg fra enhet
          </span>
          <span className="text-xs text-mist">JPG · PNG · HEIC · WebP — velg gjerne flere</span>
          <label className="mx-auto">
            <span className="ui-btn ui-btn--ghost ui-btn--sm cursor-pointer">
              <Icon name="upload" size={14} />
              Velg bilder
            </span>
            <input
              id={inputId}
              type="file"
              accept="image/*,.heic,.heif"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) void addFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </label>
        </div>
      </Card>

      {/* Controls */}
      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-1">
            <span className="text-sm text-parchment">
              {items.length} {items.length === 1 ? 'bilde' : 'bilder'}
            </span>
            {scanningCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-xs text-mist">
                <Spinner size={12} /> {scanningCount} analyserer…
              </span>
            )}
            {doneWithMatch > 0 && (
              <span className="ml-2">
                <Badge tone="gold">{doneWithMatch} klar til å legges inn</Badge>
              </span>
            )}
            {addedCount > 0 && (
              <span className="ml-2">
                <Badge tone="success">{addedCount} lagt inn</Badge>
              </span>
            )}
            {errorCount > 0 && (
              <span className="ml-2">
                <Badge tone="danger">{errorCount} feilet</Badge>
              </span>
            )}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={scanAll}
            disabled={pendingCount === 0 || scanningCount > 0}
          >
            {scanningCount > 0 ? 'Analyserer…' : `Analyser ${pendingCount > 0 ? pendingCount : 'alle'}`}
          </Button>
          {errorCount > 0 && (
            <Button
              size="sm"
              variant="ghost"
              iconLeft="refresh"
              onClick={retryAllFailed}
              disabled={scanningCount > 0}
            >
              Prøv alle som feilet
            </Button>
          )}
          {doneWithMatch > 0 && (
            <Button
              size="sm"
              variant="primary"
              iconLeft="add-bottle"
              disabled={addingId !== null}
              onClick={() => void addAllMatches()}
            >
              {labelQueueEnabled ? `Legg til alle treff + QR` : `Legg til alle treff`}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            iconLeft="x"
            onClick={() => setItems([])}
            disabled={addingId !== null}
          >
            Tøm
          </Button>
        </div>
      )}

      {/* Item list */}
      {items.length === 0 ? (
        <Card className="p-8">
          <EmptyState
            icon="camera"
            title="Ingen bilder valgt"
            description="Velg eller slipp etikettbilder over for å starte."
          />
        </Card>
      ) : (
        <div className="grid gap-2">
          <AnimatePresence initial={false}>
            {items.map((item) => {
              const cardData = {
                id: item.id,
                status: item.status === 'pending' ? 'scanning' as const : item.status === 'scanning' ? 'scanning' as const : item.status === 'added' ? 'added' as const : item.status === 'error' ? 'error' as const : 'done' as const,
                scanData: item.scanData,
                errorMessage: item.errorMessage,
                addedBottleId: item.addedBottleId,
                previewUrl: item.previewUrl,
              };

              // Show a "pending" row differently (scan button + remove)
              if (item.status === 'pending') {
                return (
                  <div key={item.id} className="ui-card grid grid-cols-[auto_1fr_auto] items-center gap-3 p-3">
                    <div className="relative grid h-14 w-10 shrink-0 place-items-center overflow-hidden rounded bg-ink-soft">
                      <img src={item.previewUrl} alt="" className="h-full w-full object-cover opacity-70" />
                    </div>
                    <div className="grid min-w-0">
                      <span className="font-serif text-sm text-parchment line-clamp-1">{item.file.name}</span>
                      <span className="text-xs text-mist">{(item.file.size / 1024).toFixed(0)} KB</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" onClick={() => void scanSingleItem(item)}>
                        Analyser
                      </Button>
                      <button
                        type="button"
                        className="ui-btn ui-btn--icon"
                        aria-label="Fjern"
                        onClick={() => setItems((prev) => prev.filter((it) => it.id !== item.id))}
                      >
                        <Icon name="x" size={12} />
                      </button>
                    </div>
                  </div>
                );
              }

              // Error row — show retry button in addition to the ScanResultCard
              if (item.status === 'error') {
                return (
                  <div key={item.id} className="grid gap-1">
                    <ScanResultCard
                      data={cardData}
                      labelQueueEnabled={labelQueueEnabled}
                      onConfirmMatch={(product, vintage) => handleConfirmMatch(item.id, product, vintage)}
                      onManualAdd={(prefill) => {
                        const ext = item.scanData?.extraction;
                        handleManualAdd(ext ? extractionToPrefill(ext) : prefill, item.id);
                      }}
                      onDismiss={() => setItems((prev) => prev.filter((it) => it.id !== item.id))}
                      confirmingProductId={addingId === item.id ? item.scanData?.candidates[0]?.product.basic.productId ?? null : null}
                    />
                    <div className="flex justify-end px-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        iconLeft="refresh"
                        onClick={() => void scanSingleItem(item)}
                        disabled={scanningCount > 0}
                      >
                        Prøv igjen
                      </Button>
                    </div>
                  </div>
                );
              }

              return (
                <ScanResultCard
                  key={item.id}
                  data={cardData}
                  labelQueueEnabled={labelQueueEnabled}
                  onConfirmMatch={(product, vintage) => handleConfirmMatch(item.id, product, vintage)}
                  onManualAdd={(prefill) => {
                    const ext = item.scanData?.extraction;
                    handleManualAdd(ext ? extractionToPrefill(ext) : prefill, item.id);
                  }}
                  onDismiss={() => setItems((prev) => prev.filter((it) => it.id !== item.id))}
                  confirmingProductId={addingId === item.id ? item.scanData?.candidates[0]?.product.basic.productId ?? null : null}
                />
              );
            })}
          </AnimatePresence>
        </div>
      )}

      <AddBottleDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerSourceId(null);
        }}
        prefill={drawerPrefill}
        onAdded={(bottleId) => {
          void qc.invalidateQueries({ queryKey: ['cellar.bottles'] });
          if (drawerSourceId) {
            patchItem(drawerSourceId, { status: 'added', addedBottleId: bottleId });
          }
          setDrawerOpen(false);
          setDrawerSourceId(null);
        }}
      />
    </div>
  );
}
